import type { TaskStatus } from '../interfaces/IScheduler.js';
import { addDays, mondayOf } from './model/days.js';
import type { IListItem, ListItemKind } from './model/listItem.js';
import { checklistBlocksCompletion } from '../../checklist/index.js';

export type DatePreset = 'today' | 'week' | 'all';

export interface IActivityFilters {
  date: DatePreset;
  types: ListItemKind[];
  critical: boolean;
  /** CPM critical-path membership (activities). Distinct from `critical`, which
   *  is issue-priority. Surfaced as the 属性 "Critical path" filter. */
  criticalPath: boolean;
  /** Milestones only (activities flagged `isMilestone`). Surfaced as the 属性
   *  "Milestones" filter, alongside Critical path. */
  milestone: boolean;
  safety: boolean;
  /** Late items only — an activity past its end, an issue past its due date (`isOverdue`). */
  overdue: boolean;
  status: string | null;
  search: string;
  /** Profession trades to keep (multi-select, the 属性 "Profession" filter).
   *  Empty = no profession filter. Matches `profession.tradeId`, so unassigned
   *  activities and free-text-assignee issues drop out while it's active. */
  professions: string[];
  /** Activities whose checklist still blocks completion — a checks-outstanding
   *  queue (task-checklist design, decision 10). */
  checksOutstanding: boolean;
}

export const DEFAULT_FILTERS: IActivityFilters = {
  date: 'all',
  types: [],
  critical: false,
  criticalPath: false,
  milestone: false,
  safety: false,
  overdue: false,
  status: null,
  search: '',
  professions: [],
  checksOutstanding: false,
};

/** A status the Activities & Issues list filters on: an activity's plan status
 *  (`TaskStatus`) or an issue's review state. Typed so a per-status lookup (the
 *  filter panel's icons) must cover every option. */
export type ActivityListStatus = TaskStatus | 'forReview' | 'rejected' | 'completed';

/** All selectable statuses, in display order: `planned` (both kinds), the issue
 *  review states, the activity-only `blocked`/`done`, then the issue-only
 *  `completed`. Drives the filter panel's status sections. */
export const STATUS_OPTIONS: readonly ActivityListStatus[] = [
  'planned',
  'forReview',
  'rejected',
  'blocked',
  'done',
  'completed',
];

/** Statuses an ACTIVITY (schedule task) can have — the subset the Progress
 *  Tracker's status filter shows, since the tracker only lists activities. Typed
 *  by the canonical `TaskStatus` union, so renaming/removing a task status breaks
 *  the build here instead of silently drifting. */
export const ACTIVITY_STATUS_OPTIONS: readonly TaskStatus[] = ['planned', 'blocked', 'done'];

/** The tracker only knows activity statuses; an issue-only status carried over from
 *  the Daily List filter (e.g. "For Review") matches no task and would silently
 *  zero the matrix, so treat it as unset there. Null passes through unchanged. */
export function activityStatusFilter(status: string | null): string | null {
  return ACTIVITY_STATUS_OPTIONS.some((s) => s === status) ? status : null;
}

/**
 * The item type(s) a status belongs to, for the status→type coupling: picking a
 * status that only one kind can have also narrows the type filter to that kind
 * (e.g. "For Review" → issues only; `completed` is an issue state, an activity's
 * is `done`). Returns null for the status both kinds share (`planned`) or when
 * cleared — those leave the type filter untouched.
 */
export function typesForStatus(status: string | null): ListItemKind[] | null {
  switch (status) {
    case 'blocked':
    case 'done':
      return ['activity'];
    case 'forReview':
    case 'rejected':
    case 'completed':
      return ['issue'];
    default:
      return null;
  }
}

// Today on the PROJECT's calendar — it lives with the site's clock in
// utils/siteTime; re-exported so this barrel keeps offering it.
export { projectToday } from '../utils/siteTime.js';

/**
 * [firstWorkingDay, lastWorkingDay] of the ISO week (Mon–Sun) containing `today`,
 * given the site's rest days. Falls back to the full Mon–Sun span when the whole
 * week is rest days or rest days are unknown (empty set).
 */
function workingWeek(today: string, restDays: Set<number>): [string, string] {
  const monday = mondayOf(today);
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(monday, i)); // Mon..Sun
  const working = weekDays.filter((d) => !restDays.has(new Date(`${d}T00:00:00Z`).getUTCDay()));
  if (working.length === 0) return [monday, addDays(monday, 6)];
  return [working[0]!, working[working.length - 1]!];
}

function windowFor(preset: 'today' | 'week', today: string, restDays: Set<number>): [string, string] {
  return preset === 'today' ? [today, today] : workingWeek(today, restDays);
}

/** The [start, end] date window for a preset, or `null` for 'all' (no window).
 *  Shared by the Daily List filter and the Progress Tracker column filter. */
export function dateWindowFor(preset: DatePreset, today: string, restDays: Set<number>): [string, string] | null {
  return preset === 'all' ? null : windowFor(preset, today, restDays);
}

/** Does the item's active range [start, end] OVERLAP the [from, to] window? (An
 *  item counts as in-window when its span intersects the preset, not merely when its
 *  start falls inside.) YYYY-MM-DD strings compare chronologically. Shared by the
 *  Daily List filter and the Progress Tracker so both agree on "in window". */
export function windowOverlaps(start: string, end: string, window: readonly [string, string]): boolean {
  return !(start > window[1] || end < window[0]);
}

export function filterListItems(
  items: readonly IListItem[],
  f: IActivityFilters,
  today: string,
  restDays: Set<number> = new Set<number>(),
): IListItem[] {
  const q = f.search.trim().toLowerCase();
  // Resolve the date window ONCE, not per item — at a few thousand items the
  // per-item Date math in workingWeek() was a measurable slice of the filter.
  // An item is "in window" when its active range OVERLAPS the preset window (not
  // merely when its start falls inside): activities span [start, end], issues are
  // a point at their due/created date. YYYY-MM-DD strings compare chronologically.
  const dateWindow = dateWindowFor(f.date, today, restDays);
  return items.filter((i) => {
    if (dateWindow && !windowOverlaps(i.start ?? i.date, i.end ?? i.date, dateWindow)) return false;
    if (f.types.length > 0 && !f.types.includes(i.kind)) return false;
    if (f.critical && !i.isCritical) return false;
    if (f.criticalPath && !i.isOnCriticalPath) return false;
    if (f.milestone && !i.isMilestone) return false;
    if (f.safety && !i.isSafety) return false;
    if (f.overdue && !i.isOverdue) return false;
    if (f.status && i.status !== f.status) return false;
    if (f.professions.length > 0 && !(i.profession && f.professions.includes(i.profession.tradeId))) return false;
    if (f.checksOutstanding && !checklistBlocksCompletion(i.raw.activity?.checklist)) return false;
    if (q) {
      const hay = `${i.title} ${i.assigneeName ?? ''}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
}

export function countActive(f: IActivityFilters): number {
  let n = 0;
  if (f.date !== 'all') n++;
  // `types` is deliberately NOT counted: it's the list's kind scope
  // (the All | Activities | Issues tabs above the feed), not a filter —
  // a chosen tab shouldn't light the filter badge or the Clear button.
  if (f.critical) n++;
  if (f.criticalPath) n++;
  if (f.milestone) n++;
  if (f.safety) n++;
  if (f.overdue) n++;
  if (f.status) n++;
  if (f.professions.length) n++;
  if (f.checksOutstanding) n++;
  if (f.search.trim()) n++;
  return n;
}
